import { useCallback, useMemo } from 'react';
import { Screen, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Box } from '~/commonComponents';
import { BackButton } from '~/commonComponents/BackButton';
import {
  fireHelpAboutClicked,
  fireHelpHowItWorksClicked,
  fireHelpDashboardTutorialClicked,
  fireHelpShiftRequestTutorialClicked,
  fireHelpProfessionalHubTutorialClicked,
  fireHelpFavoritesTutorialClicked,
} from '~/posthogs/events';
import { routes } from '~/routes';
import { NumberUtils } from '~/utils/NumberUtils';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import { TOfficeTutorial, useOfficeTutorial } from '../OfficeTutorialPopup';
import { HelpMenuLink } from './HelpMenuLink';
import { HelpSection } from './HelpSection';

const TUTORIAL_EVENT_MATCHERS: Array<{ match: (title: string) => boolean; fire: () => void }> = [
  { match: (t) => t.toLowerCase().includes('dashboard'), fire: fireHelpDashboardTutorialClicked },
  { match: (t) => t.toLowerCase().includes('shift'), fire: fireHelpShiftRequestTutorialClicked },
  { match: (t) => t.toLowerCase().includes('professional'), fire: fireHelpProfessionalHubTutorialClicked },
  { match: (t) => t.toLowerCase().includes('favorite'), fire: fireHelpFavoritesTutorialClicked },
];

export const HelpScreen = () => {
  const controls = useOfficeTutorial();
  const tutorials = RemoteConfigUtils.getOfficeTutorials() as TOfficeTutorial[];
  const csPhoneNumber = useMemo(() => NumberUtils.getCSPhone(), []);

  const handleTutorialClick = useCallback((tutorial: TOfficeTutorial) => {
    TUTORIAL_EVENT_MATCHERS.find((m) => m.match(tutorial.title))?.fire();
    controls.setTutorial(tutorial);
  }, [controls]);

  return (
    <Screen variant="standard">
      <Screen.Header start={<BackButton />} title="Help Center" />
      <Screen.Body>
        <Box
          backgroundColor={colors.neutral[50]}
          flex={1}
          marginHorizontal={56}
          paddingBottom={80}
          paddingHorizontal={40}
          paddingTop={40}
          width="auto"
          gap={12}
          marginBottom={60}
        >
          <HelpSection title="About GoTu">
            <HelpMenuLink href={routes.about} text="About" onClick={fireHelpAboutClicked} />
            <HelpMenuLink href={routes.howItWorks} text="How it Works" onClick={fireHelpHowItWorksClicked} />
          </HelpSection>

          <HelpSection title="GoTu Tutorials">
            {tutorials
              .sort((a, b) => a.position - b.position)
              .map((tutorial) => (
                <HelpMenuLink
                  key={tutorial.id}
                  text={tutorial.title}
                  onClick={() => handleTutorialClick(tutorial)}
                />
              ))}
          </HelpSection>

          <HelpSection title="Contact Us">
            <Typography as="span" variant="body1">
              Customer Service Text/Call:
              <Typography as="span" bold variant="body1">{` ${csPhoneNumber.formatted}`}</Typography>
            </Typography>
          </HelpSection>
        </Box>
      </Screen.Body>
    </Screen>
  );
};
